import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, TextField, Alert, CircularProgress,
} from "@mui/material";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

const DIALPAD_PINK = "#E63888";

/**
 * Prompts the user to save a phone number to a chemist's contact numbers.
 *
 * Props:
 *   open          — boolean
 *   onClose       — () => void  (called on Skip or after Save)
 *   phone         — string  (the number to save, e.g. "+61412345678")
 *   chemistUUID   — string  (PHUUID / ChemistUUID for the API)
 *   chemistName   — string  (displayed in the prompt)
 */
const SavePhoneDialog = ({ open, onClose, phone, chemistUUID, chemistName }) => {
  const [contactName, setContactName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const reset = () => {
    setContactName("");
    setSaving(false);
    setError(null);
    setDone(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await phappsRequest(API_PATHS.ADMIN_CHEMIST_NUMBERS, "POST", {
        ChemistUUID: chemistUUID,
        phone,
        contact_name: contactName.trim() || null,
        position: null,
      });
      setDone(true);
      // Auto-close after brief success moment
      setTimeout(() => {
        reset();
        onClose();
      }, 1000);
    } catch {
      setError("Failed to save the phone number. Please try again.");
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, color: DIALPAD_PINK, pb: 1, borderBottom: "1px solid #e5e7eb" }}>
        Save phone number?
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {done ? (
          <Box sx={{ py: 1, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "#059669", fontWeight: 600 }}>
              ✓ Number saved successfully.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Phone number display */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, p: 1.25, borderRadius: 1.5, backgroundColor: "#f9fafb", border: "1px solid #e5e7eb" }}>
              <PhoneAndroidIcon sx={{ color: DIALPAD_PINK, fontSize: 20 }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{phone}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Will be added to <strong>{chemistName}</strong>'s contact numbers
                </Typography>
              </Box>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
              Contact name <em>(optional)</em>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter the name"
              value={contactName}
              onChange={e => setContactName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !saving && handleSave()}
              autoFocus
            />

            {error && (
              <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>{error}</Alert>
            )}
          </>
        )}
      </DialogContent>

      {!done && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ textTransform: "none" }}>
            Skip
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleSave}
            startIcon={saving ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : null}
            sx={{ textTransform: "none", fontWeight: 600, backgroundColor: DIALPAD_PINK, "&:hover": { backgroundColor: "#c42d72" } }}
          >
            {saving ? "Saving…" : "Save number"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default SavePhoneDialog;
